import { createClient, type Session } from "@supabase/supabase-js";

type AppSupabase = ReturnType<typeof createClient>;

const url = import.meta.env.VITE_SUPABASE_URL;
const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;

const app = document.querySelector<HTMLElement>("#app");
if (!app) throw new Error("#app missing");

function showMessage(html: string, cls: "" | "ok" | "err" = "") {
  const el = document.createElement("p");
  el.className = `msg ${cls}`.trim();
  el.innerHTML = html;
  app.appendChild(el);
}

function renderPaidSuccess(tier: string, supabase: AppSupabase) {
  app.innerHTML = "";
  const returnTo = sanitizeReturnTo(
    new URLSearchParams(window.location.search).get("return_to"),
  );
  const card = document.createElement("div");
  card.className = "card";
  card.innerHTML = `
    <h2 style="margin:0 0 0.5rem;font-size:1.1rem">Gracias por tu compra</h2>
    <p class="msg ok" style="margin:0">
      Tu acceso premium (${tier === "1y" ? "1 año" : "1 mes"}) se activará en unos segundos
      cuando confirmemos el pago. Abre la app <strong>Elegibles</strong>, inicia sesión con la
      misma cuenta y pulsa actualizar o vuelve a entrar a Perfil.
    </p>
    <p class="msg" style="margin-top:0.75rem">
      Si no ves premium pasados unos minutos, revisa el comprobante de Wompi o escribe a soporte.
    </p>
    ${returnTo ? '<button type="button" class="btn-primary" id="btn-return-app">Volver a la app</button>' : ""}
    <button type="button" class="btn-secondary" id="btn-sign-out-paid">Cerrar sesión</button>
  `;
  if (returnTo) {
    card.querySelector("#btn-return-app")!.addEventListener("click", () => {
      window.location.href = returnTo;
    });
  }
  card.querySelector("#btn-sign-out-paid")!.addEventListener("click", () => {
    void supabase.auth.signOut();
  });
  app.appendChild(card);
}

function renderCanceled() {
  app.innerHTML = "";
  const card = document.createElement("div");
  card.className = "card";
  card.innerHTML = `
    <p class="msg" style="margin:0">Compra cancelada. Puedes intentar de nuevo cuando quieras.</p>
  `;
  app.appendChild(card);
}

const LIST_COP_1M = 29_900;
const LIST_COP_1Y = 199_900;

/** Origen de retorno cross-domain desde app.elegibles.app (query `return_to`). */
let appReturnTo: string | null = null;

function sanitizeReturnTo(raw: string | null): string | null {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return null;
  try {
    const u = new URL(trimmed);
    if (u.protocol !== "https:" && u.protocol !== "http:") return null;
    const host = u.hostname.toLowerCase();
    if (
      host === "app.elegibles.app" ||
      host === "localhost" ||
      host === "127.0.0.1"
    ) {
      return u.origin;
    }
    return null;
  } catch {
    return null;
  }
}

function readReturnToFromUrl(): string | null {
  return sanitizeReturnTo(
    new URLSearchParams(window.location.search).get("return_to"),
  );
}

function formatCopFromCents(cents: number): string {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

type TierPricing = {
  list_amount_cents: number;
  paid_amount_cents: number;
  discount_cents: number;
};

type AppliedCreatorCode = {
  code: string;
  creatorDisplayName: string;
  discountPercent: number;
  pricing: { "1m": TierPricing; "1y": TierPricing };
};

function formatEsCo(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("es-CO", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

type PremiumOverlap = {
  subActive: boolean;
  subUntil: string | null;
  prepaidUntil: string | null;
  promoUntil: string | null;
  marketingWeekendUntil: string | null;
};

function marketingWeekendPremiumActive(row: {
  enabled?: boolean;
  valid_until?: string | null;
} | null): string | null {
  if (!row || row.enabled !== true || !row.valid_until) return null;
  const t = new Date(row.valid_until).getTime();
  if (Number.isNaN(t) || t <= Date.now()) return null;
  return row.valid_until;
}

async function fetchPremiumOverlap(
  supabase: AppSupabase,
): Promise<PremiumOverlap> {
  const now = Date.now();
  let marketingWeekendUntil: string | null = null;

  const { data: flag } = await supabase
    .from("app_feature_flags")
    .select("enabled, valid_until")
    .eq("key", "marketing_weekend_premium")
    .maybeSingle();
  marketingWeekendUntil = marketingWeekendPremiumActive(
    flag as { enabled?: boolean; valid_until?: string | null } | null,
  );

  let subActive = false;
  let subUntil: string | null = null;

  const { data: ue } = await supabase
    .from("user_entitlements")
    .select("is_premium, current_period_end")
    .maybeSingle();

  if (ue?.is_premium === true) {
    const end = ue.current_period_end as string | null | undefined;
    if (!end) {
      subActive = true;
    } else {
      const t = new Date(end).getTime();
      if (!Number.isNaN(t) && t > now) {
        subActive = true;
        subUntil = end;
      }
    }
  }

  let prepaidUntil: string | null = null;
  const { data: web } = await supabase
    .from("web_prepaid_grant")
    .select("valid_until")
    .maybeSingle();
  if (web?.valid_until) {
    const t = new Date(web.valid_until as string).getTime();
    if (!Number.isNaN(t) && t > now) prepaidUntil = web.valid_until as string;
  }

  let promoUntil: string | null = null;
  const { data: pr } = await supabase
    .from("user_promo_grant")
    .select("valid_until")
    .maybeSingle();
  if (pr?.valid_until) {
    const t = new Date(pr.valid_until as string).getTime();
    if (!Number.isNaN(t) && t > now) promoUntil = pr.valid_until as string;
  }

  return { subActive, subUntil, prepaidUntil, promoUntil, marketingWeekendUntil };
}

function overlapConfirmMessage(o: PremiumOverlap): string | null {
  const parts: string[] = [];
  if (o.marketingWeekendUntil) {
    parts.push(
      `Premium gratis de fin de semana activo hasta el ${formatEsCo(o.marketingWeekendUntil)}. Puedes usar la app sin comprar prepago web.`,
    );
  }
  if (o.subActive) {
    if (o.subUntil) {
      parts.push(
        `En tu cuenta consta suscripción por la tienda (o acceso equivalente) al menos hasta el ${formatEsCo(o.subUntil)}.`,
      );
    } else {
      parts.push(
        "En tu cuenta consta suscripción por la tienda (o acceso equivalente) activa.",
      );
    }
    parts.push(
      "El prepago web suma tiempo según las reglas del sistema; las renovaciones de la suscripción de la tienda siguen su propio calendario. Puede haber solapamiento.",
    );
  }
  if (o.prepaidUntil) {
    parts.push(
      `Ya tienes prepago web hasta el ${formatEsCo(o.prepaidUntil)}. Si completas un nuevo pago, el tiempo suele sumarse a partir de esa fecha.`,
    );
  }
  if (o.promoUntil) {
    parts.push(
      `Tienes acceso por código promocional hasta el ${formatEsCo(o.promoUntil)}. El prepago web añade otro canal de acceso; puede solaparse en el tiempo.`,
    );
  }
  if (parts.length === 0) return null;
  return (
    parts.join("\n\n") +
    "\n\n¿Quieres continuar al pago prepago web?"
  );
}

function tierButtonLabel(
  tier: "1m" | "1y",
  applied: AppliedCreatorCode | null,
): string {
  const period = tier === "1y" ? "1 año" : "1 mes";
  if (!applied) {
    const list = tier === "1y" ? LIST_COP_1Y : LIST_COP_1M;
    return `Pago único — ${period} · ${formatCopFromCents(list * 100)}`;
  }
  const p = applied.pricing[tier];
  const paid = formatCopFromCents(p.paid_amount_cents);
  const list = formatCopFromCents(p.list_amount_cents);
  return `Pago único — ${period} · ${paid} (antes ${list})`;
}

function renderBuy(supabase: AppSupabase, session: Session) {
  let appliedCode: AppliedCreatorCode | null = null;

  const initialRef = new URLSearchParams(window.location.search).get("ref") ??
    new URLSearchParams(window.location.search).get("code") ?? "";

  app.innerHTML = "";
  const card = document.createElement("div");
  card.className = "card";
  card.innerHTML = `
    <p class="msg" style="margin:0">Sesión: <strong>${session.user.email ?? session.user.id}</strong></p>
    <button type="button" class="btn-secondary" id="btn-sign-out">Cerrar sesión</button>
    <p class="msg ok" id="marketing-weekend-msg" hidden></p>
  `;
  card.querySelector("#btn-sign-out")!.addEventListener("click", () => {
    void supabase.auth.signOut();
  });
  app.appendChild(card);

  void fetchPremiumOverlap(supabase).then((overlap) => {
    if (!overlap.marketingWeekendUntil) return;
    const el = card.querySelector<HTMLElement>("#marketing-weekend-msg");
    if (!el) return;
    el.hidden = false;
    el.textContent =
      `Premium gratis este fin de semana hasta el ${formatEsCo(overlap.marketingWeekendUntil)}. ` +
      "Abre la app Elegibles con esta misma cuenta.";
  });

  const promoCard = document.createElement("div");
  promoCard.className = "card";
  promoCard.innerHTML = `
    <label for="creator-code">Código de creador (opcional)</label>
    <input type="text" id="creator-code" autocomplete="off" placeholder="Ej. MARIA15" maxlength="32" />
    <button type="button" class="btn-secondary" id="btn-apply-code">Aplicar código</button>
    <p class="msg" id="creator-code-msg"></p>
  `;
  app.appendChild(promoCard);

  const wrap = document.createElement("div");
  wrap.className = "card";
  wrap.id = "tier-buttons";

  const refreshTierButtons = () => {
    wrap.innerHTML = "";
    const mkBtn = (tier: "1m" | "1y") => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "btn-tier";
      b.textContent = tierButtonLabel(tier, appliedCode);
      b.addEventListener("click", () =>
        void onTierCheckoutClick(supabase, session, tier, appliedCode?.code ?? null),
      );
      wrap.appendChild(b);
    };
    mkBtn("1m");
    mkBtn("1y");
  };
  refreshTierButtons();
  app.appendChild(wrap);

  const out = document.createElement("p");
  out.className = "msg";
  out.id = "checkout-msg";
  app.appendChild(out);

  const codeInput = promoCard.querySelector<HTMLInputElement>("#creator-code")!;
  const codeMsg = promoCard.querySelector<HTMLElement>("#creator-code-msg")!;

  async function applyCreatorCode(raw: string) {
    const trimmed = raw.trim();
    if (trimmed.length < 3) {
      appliedCode = null;
      codeMsg.textContent = "";
      refreshTierButtons();
      return;
    }
    codeMsg.textContent = "Validando código…";
    codeMsg.className = "msg";
    const fnUrl =
      `${url!.replace(/\/+$/, "")}/functions/v1/preview-creator-web-code`;
    let res: Response;
    try {
      res = await fetch(fnUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          apikey: anon!,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ creatorCode: trimmed }),
      });
    } catch {
      appliedCode = null;
      codeMsg.textContent = "No se pudo validar el código (red).";
      codeMsg.className = "msg err";
      refreshTierButtons();
      return;
    }
    const data = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      message?: string;
      creator_display_name?: string;
      discount_percent?: number;
      pricing?: AppliedCreatorCode["pricing"];
    };
    if (!data.ok || !data.pricing) {
      appliedCode = null;
      codeMsg.textContent = data.message ?? "Código no válido.";
      codeMsg.className = "msg err";
      refreshTierButtons();
      return;
    }
    appliedCode = {
      code: trimmed.toUpperCase(),
      creatorDisplayName: data.creator_display_name ?? "",
      discountPercent: data.discount_percent ?? 15,
      pricing: data.pricing,
    };
    codeMsg.textContent =
      `Código aplicado: −${appliedCode.discountPercent}%` +
      (appliedCode.creatorDisplayName
        ? ` · ${appliedCode.creatorDisplayName}`
        : "");
    codeMsg.className = "msg ok";
    refreshTierButtons();
  }

  promoCard.querySelector("#btn-apply-code")!.addEventListener("click", () => {
    void applyCreatorCode(codeInput.value);
  });

  if (initialRef.trim().length >= 3) {
    codeInput.value = initialRef.trim();
    void applyCreatorCode(initialRef);
  }
}

async function onTierCheckoutClick(
  supabase: AppSupabase,
  session: Session,
  tier: "1m" | "1y",
  creatorCode: string | null,
) {
  const overlap = await fetchPremiumOverlap(supabase);
  const msg = overlapConfirmMessage(overlap);
  if (msg !== null && !window.confirm(msg)) {
    return;
  }
  await startCheckout(session, tier, creatorCode);
}

async function startCheckout(
  session: Session,
  tier: "1m" | "1y",
  creatorCode: string | null,
) {
  const msg = document.querySelector("#checkout-msg");
  if (msg) {
    msg.textContent = "Abriendo checkout seguro (Wompi)…";
    msg.className = "msg";
  }
  const functionsUrl = `${url.replace(/\/+$/, "")}/functions/v1/create-wompi-checkout`;
  let res: Response;
  try {
    res = await fetch(functionsUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        apikey: anon,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        tier,
        ...(creatorCode ? { creatorCode } : {}),
        ...(appReturnTo ? { returnTo: appReturnTo } : {}),
      }),
    });
  } catch {
    if (msg) {
      msg.textContent =
        "No se pudo conectar con el servidor de pago (red o CORS). " +
        "Comprueba que WEB_STORE_ORIGIN en Supabase sea exactamente " +
        "https://premium.elegibles.app (con https://).";
      msg.className = "msg err";
    }
    return;
  }
  const data = (await res.json().catch(() => ({}))) as {
    checkoutUrl?: string;
    fields?: Record<string, string>;
    error?: string;
  };
  if (!res.ok || !data.checkoutUrl || !data.fields) {
    if (msg) {
      msg.textContent =
        data.error === "server_misconfigured"
          ? "Falta configurar Wompi en el servidor (Edge Function)."
          : `No se pudo iniciar el pago (${res.status}).`;
      msg.className = "msg err";
    }
    return;
  }
  const form = document.createElement("form");
  form.method = "GET";
  form.action = data.checkoutUrl;
  for (const [name, value] of Object.entries(data.fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = String(value);
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

function renderAuth(supabase: ReturnType<typeof createClient>) {
  app.innerHTML = "";
  const card = document.createElement("div");
  card.className = "card";
  card.innerHTML = `
    <label for="email">Correo</label>
    <input type="email" id="email" autocomplete="email" required />
    <label for="password">Contraseña</label>
    <input type="password" id="password" autocomplete="current-password" required />
    <button type="button" class="btn-primary" id="btn-in">Iniciar sesión</button>
    <button type="button" class="btn-secondary" id="btn-up">Crear cuenta</button>
    <p class="msg" id="auth-msg"></p>
  `;
  app.appendChild(card);

  const msg = () => card.querySelector<HTMLElement>("#auth-msg")!;

  card.querySelector("#btn-in")!.addEventListener("click", async () => {
    const email = (card.querySelector("#email") as HTMLInputElement).value.trim();
    const password = (card.querySelector("#password") as HTMLInputElement).value;
    msg().textContent = "Entrando…";
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      msg().textContent = error.message;
      msg().className = "msg err";
      return;
    }
    msg().textContent = "";
  });

  card.querySelector("#btn-up")!.addEventListener("click", async () => {
    const email = (card.querySelector("#email") as HTMLInputElement).value.trim();
    const password = (card.querySelector("#password") as HTMLInputElement).value;
    msg().textContent = "Creando cuenta…";
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) {
      msg().textContent = error.message;
      msg().className = "msg err";
      return;
    }
    msg().textContent =
      "Revisa tu correo para confirmar la cuenta si tu proyecto Supabase lo exige.";
    msg().className = "msg ok";
  });
}

async function main() {
  const params = new URLSearchParams(window.location.search);
  appReturnTo = readReturnToFromUrl();
  if (params.get("canceled") === "1") {
    renderCanceled();
    return;
  }
  if (!url || !anon || url.includes("TU-PROYECTO")) {
    app.innerHTML =
      '<div class="card"><p class="msg err">Configura VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY en .env (ver README).</p></div>';
    return;
  }

  const supabase = createClient(url, anon);

  if (params.get("paid") === "1") {
    const tier = params.get("tier") === "1y" ? "1y" : "1m";
    renderPaidSuccess(tier, supabase);
    supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) renderAuth(supabase);
    });
    return;
  }

  const { data } = await supabase.auth.getSession();
  if (data.session) renderBuy(supabase, data.session);
  else renderAuth(supabase);

  supabase.auth.onAuthStateChange((_event, session) => {
    if (session) renderBuy(supabase, session);
    else renderAuth(supabase);
  });
}

void main();
